import { useEditor, useEditorState, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import { TableKit } from "@tiptap/extension-table";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Link as LinkIcon,
  Table as TableIcon,
  BetweenHorizontalStart,
  BetweenHorizontalEnd,
  BetweenVerticalStart,
  BetweenVerticalEnd,
  Rows3,
  Columns3,
  PanelTop,
  Merge,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Toggle } from "@/components/ui/toggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  disabled,
}: RichTextEditorProps) => {
  const { t } = useTranslation();
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder }),
      Link.configure({ openOnClick: false }),
      // `renderWrapper` stays false so getHTML() emits a bare <table>; the
      // resizable node view still wraps it in a div while editing.
      TableKit.configure({
        table: { resizable: true, lastColumnResizable: false },
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  // Tiptap v3 does not re-render on transactions, so toolbar state has to be
  // subscribed explicitly — otherwise `isActive` stays stale until the next
  // keystroke, and moving the caret into a table never enables its menu.
  const editorState = useEditorState({
    editor,
    selector: ({ editor }) => ({
      isBold: editor.isActive("bold"),
      isItalic: editor.isActive("italic"),
      isUnderline: editor.isActive("underline"),
      isHeading2: editor.isActive("heading", { level: 2 }),
      isHeading3: editor.isActive("heading", { level: 3 }),
      isBulletList: editor.isActive("bulletList"),
      isOrderedList: editor.isActive("orderedList"),
      isLink: editor.isActive("link"),
      isTable: editor.isActive("table"),
    }),
  });

  useEffect(() => {
    if (editor && value !== undefined && editor.getHTML() !== value) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  const handleLinkToggle = () => {
    if (!editor) return;
    if (editor.isActive("link")) {
      editor.chain().focus().unsetLink().run();
    } else {
      const url = window.prompt("URL");
      if (url) {
        editor.chain().focus().setLink({ href: url }).run();
      }
    }
  };

  const inTable = editorState?.isTable ?? false;

  return (
    <div
      className={cn(
        "rounded-md border border-input bg-background text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        disabled && "opacity-50 cursor-not-allowed",
      )}
    >
      <div className="flex flex-wrap gap-1 border-b border-input p-1">
        <Toggle
          size="sm"
          pressed={editorState?.isBold ?? false}
          onPressedChange={() => editor?.chain().focus().toggleBold().run()}
          disabled={disabled}
          aria-label="Bold"
        >
          <Bold className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editorState?.isItalic ?? false}
          onPressedChange={() => editor?.chain().focus().toggleItalic().run()}
          disabled={disabled}
          aria-label="Italic"
        >
          <Italic className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editorState?.isUnderline ?? false}
          onPressedChange={() =>
            editor?.chain().focus().toggleUnderline().run()
          }
          disabled={disabled}
          aria-label="Underline"
        >
          <UnderlineIcon className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editorState?.isHeading2 ?? false}
          onPressedChange={() =>
            editor?.chain().focus().toggleHeading({ level: 2 }).run()
          }
          disabled={disabled}
          aria-label="Heading 2"
        >
          <Heading2 className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editorState?.isHeading3 ?? false}
          onPressedChange={() =>
            editor?.chain().focus().toggleHeading({ level: 3 }).run()
          }
          disabled={disabled}
          aria-label="Heading 3"
        >
          <Heading3 className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editorState?.isBulletList ?? false}
          onPressedChange={() =>
            editor?.chain().focus().toggleBulletList().run()
          }
          disabled={disabled}
          aria-label="Bullet list"
        >
          <List className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editorState?.isOrderedList ?? false}
          onPressedChange={() =>
            editor?.chain().focus().toggleOrderedList().run()
          }
          disabled={disabled}
          aria-label="Ordered list"
        >
          <ListOrdered className="h-4 w-4" />
        </Toggle>
        <Toggle
          size="sm"
          pressed={editorState?.isLink ?? false}
          onPressedChange={handleLinkToggle}
          disabled={disabled}
          aria-label="Link"
        >
          <LinkIcon className="h-4 w-4" />
        </Toggle>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={disabled}>
            {/* Controlled `pressed` with no handler: the toggle only reflects
                whether the caret sits in a table, the click opens the menu. */}
            <Toggle
              size="sm"
              pressed={inTable}
              disabled={disabled}
              aria-label={t("keys.editor.table")}
            >
              <TableIcon className="h-4 w-4" />
            </Toggle>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-60">
            <DropdownMenuItem
              onSelect={() =>
                editor
                  ?.chain()
                  .focus()
                  .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                  .run()
              }
            >
              <TableIcon />
              {t("keys.editor.insert_table")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!inTable}
              onSelect={() => editor?.chain().focus().addRowBefore().run()}
            >
              <BetweenHorizontalStart />
              {t("keys.editor.add_row_before")}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!inTable}
              onSelect={() => editor?.chain().focus().addRowAfter().run()}
            >
              <BetweenHorizontalEnd />
              {t("keys.editor.add_row_after")}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!inTable}
              onSelect={() => editor?.chain().focus().deleteRow().run()}
            >
              <Rows3 />
              {t("keys.editor.delete_row")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!inTable}
              onSelect={() => editor?.chain().focus().addColumnBefore().run()}
            >
              <BetweenVerticalStart />
              {t("keys.editor.add_column_before")}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!inTable}
              onSelect={() => editor?.chain().focus().addColumnAfter().run()}
            >
              <BetweenVerticalEnd />
              {t("keys.editor.add_column_after")}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!inTable}
              onSelect={() => editor?.chain().focus().deleteColumn().run()}
            >
              <Columns3 />
              {t("keys.editor.delete_column")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!inTable}
              onSelect={() => editor?.chain().focus().toggleHeaderRow().run()}
            >
              <PanelTop />
              {t("keys.editor.toggle_header_row")}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={!inTable}
              onSelect={() => editor?.chain().focus().mergeOrSplit().run()}
            >
              <Merge />
              {t("keys.editor.merge_or_split")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              disabled={!inTable}
              onSelect={() => editor?.chain().focus().deleteTable().run()}
            >
              <Trash2 />
              {t("keys.editor.delete_table")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <EditorContent
        editor={editor}
        className="tiptap-editor prose prose-sm max-w-none min-h-32 p-3 focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-32 [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0 [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none"
      />
    </div>
  );
};

export default RichTextEditor;
